/**
 * KillBeatDirector — the SINGLE owner of the mob kill beat (K25).
 *
 * Spec: docs/plans/fx-grammar-specs/death-and-kill-beats.md §6 step 1
 * (two-owner consolidation — behavior-preserving except de-duplication).
 *
 * Before: two racing owners played the death beat —
 *   1. RemoteMobManager.handleNPCLeave → MobRenderer.playDeathEffects
 *      (fired on ANY state removal, sweeps included),
 *   2. RemoteCastVFXHandler.onRemoteNPCDefeated (fired on the `npcDefeated`
 *      message, silently dropped when the patch removed the mob first).
 * Message-first double-played smoke/sparkle/+XP; patch-first dropped the
 * message-side beat entirely. Both signals now FEED this director, which:
 *   - dedups per mob death (key = entityId; entry cleared on rejoin, TTL 10s),
 *   - reads position from the live entity OR the DeathSnapshotStore
 *     (capture-before-removal, so either signal order lands at a valid spot),
 *   - splits despawn from death: `npcLeave` with no `npcDefeated` inside a
 *     250 ms grace window plays NO beat (sweeps stop masquerading as kills),
 *   - owns the ONE `+XP` reward text (spec §1.2 FCT doctrine),
 *   - resolves boss/elite from the snapshot, killing the live-lookup race.
 *
 * School-dissolve slice (spec §6 steps 3+4, 2026-07-17): the beat is now the
 * SCHOOL-FLAVORED death choreography — a GhostSpriteDissolve body program +
 * the grammar-composed `death_<school>` flourish (DeathVFX.ts) replacing the
 * generic smoke+sparkle, plus the attacker-side `kill_confirm_<school>` glint
 * (local killer only, zero numbers) and the soulWisp remnant riding inside
 * each death def. The `school` parameter arrives from the wire/snapshot
 * threads (spec §2.2 — separate server lane); undefined gracefully defaults
 * to the physical program.
 */

import { gameLogger } from '../../../utils/GameLogger';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { VisualLifecycleStore } from '../../diagnostics/VisualLifecycleStore';
import type { VisualEventType } from '../../diagnostics/VisualLifecycleStore';
import { CombatFeedbackManager } from '../CombatFeedbackManager';
import { DeathSnapshotStore } from '../../systems/combat/DeathSnapshotStore';
import type { DeathSnapshot } from '../../systems/combat/DeathSnapshotStore';
import { findMobPosition, findStatusTargetPosition, localSessionId } from './entityPositions';
import {
  resolveDeathStyle,
  resolveKillConfirmId,
  resolveDeathEffectId,
  ensureDeathVfxRegistered,
  DESPAWN_FADE_PROGRAM,
} from '../config/abilities/DeathVFX';
import { MOB_CONFIG } from '../../../shared/constants/EntityConfig';
import type { MobDeathConfig } from '../../../shared/constants/EntityConfig';
import { GhostSpriteDissolveSystem } from '../players/GhostSpriteDissolve';
import type { DissolveProgram } from '../players/GhostSpriteDissolve';

const DEATH_VFX_EVENT: VisualEventType = 'death_vfx';

/** `npcLeave` with no `npcDefeated` inside this window ⇒ despawn, not death. */
const DESPAWN_GRACE_MS = 250;
/** Dedup entries self-prune after this long (prune-on-call, no timers). */
const ENTRY_TTL_MS = 10_000;

export interface NpcDefeatedMsg {
  npcId: string;
  npcName?: string;
  defeatedBy?: string;
  xpGained?: number;
}

export interface KillBeatDeps {
  /** Legacy particle fallback (VFXSystem.spawnEffect) — the smoke puff. */
  spawnEffect: (effectId: string, x: number, y: number) => void;
  /** Data-driven effect play (VFXEffectPlayer.playById) — the sparkle burst.
   *  `overrides` carries the TOML death-table scale multiplier (§3.2); dep
   *  installers that don't forward it degrade gracefully to scale 1. */
  playById: (effectId: string, x: number, y: number, overrides?: { scale?: number }) => void;
  /** Owner-1 parity: playDeathEffects carried screenShake(3, 300). */
  screenShake: (intensity: number, durationMs: number) => void;
  triggerFinisher: (x: number, y: number) => void;
  triggerReward: (x: number, y: number, text: string) => void;
  /** Live-entity position lookup (null once the mob is removed). */
  findPosition: (id: string) => { x: number; y: number } | null;
  /** Live-mob boss/elite lookup — same source the old owner 2 used. */
  getLiveMobType: (id: string) => string | undefined;
  /** Ghost-body dissolve (GhostSpriteDissolve) — false ⇒ flourish-only beat. */
  dissolveGhost: (id: string, snapshot: DeathSnapshot, program: DissolveProgram) => boolean;
  /** Early ghost kill — the same id respawned (risk-table overlap guard). */
  cancelGhost: (id: string) => void;
  /** Local Colyseus session id — killConfirm is local-killer-only (§1.2). */
  localSessionId: () => string | null;
  /** Local player world position — the killConfirm anchor. */
  getLocalPlayerPosition: () => { x: number; y: number } | null;
  /** Compiled `[<mob>.death]` TOML override (spec §3.2) for this entity's mob
   *  definition — undefined = full default derivation. The default impl
   *  resolves the def id from the live mob (`enemyType`) or, post-removal,
   *  from the snapshot's textureKey (`mob_<defId>_<npcId>`). */
  getDeathOverride: (id: string) => MobDeathConfig | undefined;
  /** Retire the live body on the kill message: capture its full death
   *  snapshot (texture key, facing, size) and remove it, so the dissolve
   *  starts now instead of when the removal patch lands. False = no live
   *  body. RemoteMobManager installs it; the default is a no-op (lane AF). */
  retireLiveBody: (id: string) => boolean;
}

type EntryStatus =
  /** `npcDefeated` seen but no position resolvable yet — beat plays when the
   *  leave signal writes the snapshot. */
  | 'defeat-waiting-pos'
  /** `npcLeave` seen, despawn grace timer running. */
  | 'leave-grace'
  /** Beat played — any further signal for this death is a suppressed dup. */
  | 'beaten'
  /** Grace expired with no kill message — resolved as a despawn (no beat).
   *  A late `npcDefeated` still upgrades this to a beat via the snapshot. */
  | 'despawned';

interface Entry {
  status: EntryStatus;
  at: number;
  msg?: NpcDefeatedMsg;
  graceTimer?: ReturnType<typeof setTimeout>;
}

function defaultDeps(): KillBeatDeps {
  const vfx = (): any => {
    try {
      return ServiceRegistry.getInstance().get('VFXSystem', { optional: true });
    } catch {
      return null;
    }
  };
  return {
    spawnEffect: (id, x, y) => vfx()?.spawnEffect?.(id, x, y),
    playById: (id, x, y, overrides) => vfx()?.playById?.(id, x, y, overrides),
    screenShake: (intensity, durationMs) => vfx()?.screenShake?.(intensity, durationMs),
    triggerFinisher: (x, y) => CombatFeedbackManager.getInstance().triggerFinisher(x, y),
    triggerReward: (x, y, text) => CombatFeedbackManager.getInstance().triggerReward(x, y, text),
    findPosition: findMobPosition,
    getLiveMobType: (id) => {
      try {
        const mob = ServiceRegistry.getInstance()
          .get('MobSystem', { optional: true })?.getManager?.()?.getMob?.(id);
        return mob?.getData?.('mobType');
      } catch {
        return undefined;
      }
    },
    dissolveGhost: (id, snapshot, program) =>
      GhostSpriteDissolveSystem.getInstance().dissolve(id, snapshot, program),
    cancelGhost: (id) => GhostSpriteDissolveSystem.getInstance().cancel(id),
    localSessionId: () => localSessionId(),
    // Empty target id short-circuits to the local player (the common-case
    // branch in findStatusTargetPosition).
    getLocalPlayerPosition: () => findStatusTargetPosition(''),
    getDeathOverride: (id) => {
      try {
        // Live path: the mob still exists — its enemyType IS the mob def id.
        const mob = ServiceRegistry.getInstance()
          .get('MobSystem', { optional: true })?.getManager?.()?.getMob?.(id);
        let defId: string | undefined = mob?.enemyType;
        if (!defId) {
          // Post-removal path: parse the def id back out of the snapshot's
          // textureKey (`mob_<defId>_<npcId>`, SpriteTextureSystem.generateMobKey).
          const key = DeathSnapshotStore.get(id)?.textureKey;
          if (key && key.startsWith('mob_') && key.endsWith(`_${id}`)) {
            defId = key.slice(4, key.length - id.length - 1) || undefined;
          }
        }
        return defId ? MOB_CONFIG[defId]?.death : undefined;
      } catch {
        return undefined;
      }
    },
    retireLiveBody: () => false,
  };
}

export class KillBeatDirector {
  private static instance: KillBeatDirector | null = null;

  private deps: KillBeatDeps;
  private entries = new Map<string, Entry>();

  /** Tests construct directly with full mock deps; runtime uses getInstance(). */
  constructor(deps?: Partial<KillBeatDeps>) {
    this.deps = { ...defaultDeps(), ...deps };
    // Self-registration keeps the death_* / kill_confirm_* defs playable until
    // the conductor wires DEATH_VFX into the VFXAbilityDefs aggregator
    // (idempotent — the registry overwrite is a no-op with a debug log).
    ensureDeathVfxRegistered();
  }

  public static getInstance(): KillBeatDirector {
    if (!KillBeatDirector.instance) {
      KillBeatDirector.instance = new KillBeatDirector();
    }
    return KillBeatDirector.instance;
  }

  /** Merge dep overrides (RemoteCastVFXHandler installs its canonical spawn
   *  funnels here so both screens keep drawing through the same pipeline). */
  public install(overrides: Partial<KillBeatDeps>): void {
    this.deps = { ...this.deps, ...overrides };
  }

  /** The `npcDefeated` direct message — the only signal that means "killed".
   *  `school` is the future killing-blow thread (spec §2.2); undefined today. */
  public onNpcDefeated(msg: NpcDefeatedMsg, school?: string): void {
    const npcId = msg?.npcId;
    if (!npcId) return;
    this.prune();

    const entry = this.entries.get(npcId);
    if (entry?.status === 'beaten') {
      this.recordSuppressed(npcId, 'npcDefeated');
      return;
    }

    const pos = this.resolvePosition(npcId);
    if (!pos) {
      // Mob never tracked locally and no snapshot yet — hold the message;
      // the leave signal writes the snapshot and replays it (or TTL prunes).
      this.setEntry(npcId, { status: 'defeat-waiting-pos', at: Date.now(), msg });
      gameLogger.debug('vfx', `[KillBeatDirector] npcDefeated for ${npcId} with no position yet — deferred`);
      return;
    }

    // The message normally beats the removal patch. Retire the live body now
    // (its snapshot keeps texture, type and death table) so the beat's ghost
    // dissolve starts on this first death signal instead of a standing body
    // that pops when the patch lands (lane AF, 2026-10-08).
    this.deps.retireLiveBody(npcId);
    this.playBeat(npcId, pos, msg, 'npcDefeated', school);
  }

  /** The `npcs.onRemove` patch — means "gone" (killed OR swept). Callers must
   *  write the DeathSnapshotStore BEFORE removal, then call this. */
  public onNpcLeave(npcId: string): void {
    if (!npcId) return;
    this.prune();

    const entry = this.entries.get(npcId);
    if (entry?.status === 'beaten') {
      this.recordSuppressed(npcId, 'npcLeave');
      return;
    }

    if (entry?.status === 'defeat-waiting-pos') {
      // Message arrived first but had nowhere to anchor — the snapshot the
      // caller just wrote is the anchor. Play now.
      const pos = this.resolvePosition(npcId);
      if (pos) {
        this.playBeat(npcId, pos, entry.msg ?? { npcId }, 'npcLeave');
      }
      return;
    }

    // Removal with no kill message (yet): despawn ≠ death. Give the message
    // a grace window; if none arrives this was a sweep — no beat, no XP.
    const graceTimer = setTimeout(() => {
      const e = this.entries.get(npcId);
      if (!e || e.status !== 'leave-grace') return;
      this.setEntry(npcId, { status: 'despawned', at: Date.now() });
      VisualLifecycleStore.record({
        type: DEATH_VFX_EVENT,
        label: `${npcId} despawn (no beat)`,
        x: DeathSnapshotStore.get(npcId)?.x ?? 0,
        y: DeathSnapshotStore.get(npcId)?.y ?? 0,
        targetId: npcId,
        source: 'kill-beat-director',
        meta: { trigger: 'npcLeave', despawn: true },
      });
      gameLogger.debug('vfx', `[KillBeatDirector] ${npcId} removed without npcDefeated — despawn, no beat`);
    }, DESPAWN_GRACE_MS);
    this.setEntry(npcId, { status: 'leave-grace', at: Date.now(), graceTimer });
  }

  /** A (re)joining entity resets its dedup entry so the NEXT death of the
   *  same id (respawning mobs reuse ids) beats again. Also kills any ghost
   *  still dissolving for that id — a respawned body must never overlap its
   *  own corpse (spec risk table). */
  public onNpcJoin(npcId: string): void {
    this.clearEntry(npcId);
    this.deps.cancelGhost(npcId);
  }

  /** Full teardown (disconnect / dispose). */
  public reset(): void {
    for (const id of Array.from(this.entries.keys())) {
      this.deps.cancelGhost(id);
      this.clearEntry(id);
    }
  }

  // ── internals ─────────────────────────────────────────────────────

  private playBeat(
    npcId: string,
    pos: { x: number; y: number },
    msg: NpcDefeatedMsg,
    trigger: 'npcDefeated' | 'npcLeave',
    school?: string,
  ): void {
    const snapshot = DeathSnapshotStore.get(npcId);
    // Snapshot first: captured before removal, so the boss finisher no longer
    // depends on winning the race against the state deletion (spec §0.5).
    const mobType = snapshot?.mobType ?? this.deps.getLiveMobType(npcId);
    const isBossOrElite = mobType === 'boss' || mobType === 'elite';
    // §3.2 default derivation: training dummies (respawn_time = 0) get the
    // despawn-fade read only — no school flourish, no soulWisp overlap.
    const isTraining = mobType === 'training';
    const tier = isBossOrElite ? mobType : isTraining ? 'training' : 'normal';

    // TOML `[<mob>.death]` override (§3.2) — consumed ON TOP of the default
    // derivation: style forces the school-style, durationMs stretches the
    // ghost program, scale multiplies the flourish, soulWisp=false strips
    // the remnant. Absent table = every default below unchanged.
    const override = this.deps.getDeathOverride(npcId);

    // School precedence (§3.2): TOML `style` > explicit param (wire thread,
    // §2.2) > snapshot killing-blow school (§2.1 write-site 1) > 'physical'.
    const style = resolveDeathStyle(override?.style ?? school ?? snapshot?.lastSchool);
    const program = !isTraining && override?.durationMs != null
      ? { ...style.program, durationMs: override.durationMs }
      : style.program;

    // Tier A body: ghost-sprite dissolve from the cached texture (§1.1).
    // False ⇒ flourish-only fallback (texture cache miss / headless), which
    // the lifecycle meta records so silence is never ambiguous (risk table).
    const ghost = snapshot
      ? this.deps.dissolveGhost(npcId, snapshot, isTraining ? DESPAWN_FADE_PROGRAM : program)
      : false;

    const effectId = isTraining
      ? style.effectId // label-only; training plays no flourish
      : resolveDeathEffectId(style, override?.soulWisp !== false);
    if (!isTraining) {
      // Tier A flourish: the school-flavored grammar def (soulWisp rides
      // inside it, delayed past the body-read peak — §1.3; stripped when the
      // TOML sets soul_wisp = false).
      if (override?.scale != null) {
        this.deps.playById(effectId, pos.x, pos.y, { scale: override.scale });
      } else {
        this.deps.playById(effectId, pos.x, pos.y);
      }
      if (isBossOrElite) {
        // Boss/elite keep the finisher ON TOP of the school beat, still
        // race-free via the snapshot. In-school stagedBlast + ×1.3 elite
        // variants are the deferred boss-tier slice (spec §6 step 5).
        this.deps.triggerFinisher(pos.x, pos.y);
      }
    }
    // Owner-1 parity: every real death carries exactly one screenShake(3, 300)
    // — the elite/boss-only shake policy is held on DJ gate (spec §8 Q2).
    this.deps.screenShake(3, 300);

    const xpGained = msg.xpGained;
    if (xpGained) {
      // The single +XP owner (spec §1.2) — same call/offset owner 2 used.
      this.deps.triggerReward(pos.x, pos.y - 20, `+${xpGained} XP`);
    }

    // Tier B: killConfirm — the attacker's personal glint, local killer only,
    // ZERO numbers rendered (one-FCT-owner doctrine, §1.2).
    let killConfirm = false;
    if (!isTraining && msg.defeatedBy && msg.defeatedBy === this.deps.localSessionId()) {
      const attacker = this.deps.getLocalPlayerPosition();
      if (attacker) {
        this.deps.playById(resolveKillConfirmId(style.school), attacker.x, attacker.y);
        killConfirm = true;
      }
    }

    const entry = this.entries.get(npcId);
    if (entry?.graceTimer) clearTimeout(entry.graceTimer);
    this.setEntry(npcId, { status: 'beaten', at: Date.now() });

    VisualLifecycleStore.record({
      type: DEATH_VFX_EVENT,
      label: `${npcId} kill beat (${isTraining ? 'training fade' : effectId}${isBossOrElite ? ' + finisher' : ''})`,
      x: pos.x,
      y: pos.y,
      targetId: npcId,
      school: style.school,
      source: 'kill-beat-director',
      meta: {
        trigger,
        mobType: mobType ?? null,
        xpGained: xpGained ?? 0,
        effectId: isTraining ? null : effectId,
        tier,
        soulWisp: !isTraining && override?.soulWisp !== false,
        ghost,
        killConfirm,
        // TOML death-table override, surfaced so JSON-first VQA can tell an
        // authored beat from the default derivation (null = no table).
        deathOverride: override ?? null,
      },
    });
  }

  private resolvePosition(npcId: string): { x: number; y: number } | null {
    const live = this.deps.findPosition(npcId);
    if (live) return live;
    const snap = DeathSnapshotStore.get(npcId);
    return snap ? { x: snap.x, y: snap.y } : null;
  }

  private recordSuppressed(npcId: string, trigger: 'npcDefeated' | 'npcLeave'): void {
    const snap = DeathSnapshotStore.get(npcId);
    VisualLifecycleStore.record({
      type: DEATH_VFX_EVENT,
      label: `${npcId} kill beat suppressed (${trigger} after beat)`,
      x: snap?.x ?? 0,
      y: snap?.y ?? 0,
      targetId: npcId,
      source: 'kill-beat-director',
      meta: { trigger, suppressed: 'duplicate' },
    });
  }

  private setEntry(npcId: string, entry: Entry): void {
    const prior = this.entries.get(npcId);
    if (prior?.graceTimer && prior.graceTimer !== entry.graceTimer) {
      clearTimeout(prior.graceTimer);
    }
    this.entries.set(npcId, entry);
  }

  private clearEntry(npcId: string): void {
    const entry = this.entries.get(npcId);
    if (entry?.graceTimer) clearTimeout(entry.graceTimer);
    this.entries.delete(npcId);
  }

  private prune(): void {
    const now = Date.now();
    for (const [id, entry] of this.entries) {
      if (now - entry.at > ENTRY_TTL_MS && entry.status !== 'leave-grace') {
        this.clearEntry(id);
      }
    }
  }
}
